import { AxiosResponse } from 'axios'

import type { Dayjs } from 'configuration/dayjs'
import {
    CurrencyMap,
    OrganizationPermission,
    User,
    UserPermission,
} from 'types'

import { unscopedApiClient } from '../../unscopedApiClient'
import { getCerebroHeaders } from '../headers'
import { CerebroApiOptions } from '../types'

export const getCurrentUser = (): Promise<AxiosResponse<User>> =>
    unscopedApiClient.get(`/api/user/`)

export const getUserFeaturePermissions = (): Promise<
    AxiosResponse<UserPermission[]>
> => unscopedApiClient.get('/api/feature_permissions/')

export const getUserOrganizationFeaturePermissions = (
    organizationId: number
): Promise<AxiosResponse<OrganizationPermission[]>> =>
    unscopedApiClient.get('/api/feature_permissions/', {
        headers: {
            'x-dsi-org-id': organizationId,
        },
        params: { ordering: 'friendly_name' },
    })

export const getUserOrganizationGroupFeaturePermissions = (
    organizationGroupId: number
): Promise<AxiosResponse<OrganizationPermission[]>> =>
    unscopedApiClient.get('/api/feature_permissions/', {
        headers: {
            'x-dsi-org-group-id': organizationGroupId,
        },
        params: { ordering: 'friendly_name' },
    })

interface HomePageMetricsSetting {
    [organizationId: number]: {
        performanceMetrics?: string[]
        keyMetrics?: string[]
    }
}

export interface UserSettings {
    organizationId?: number
    organizationGroupId?: number
    feedbackSurveyLastRead?: string
    latestNewFeatureId?: string | null
    hiddenAutomationCapabilities?: string[]
    currency?: CurrencyMap
    homeDashboardId?: string
    homePageMetrics?: HomePageMetricsSetting
    gilId?: string
    lastUpdated?: Dayjs
}

export const getUserSettings = async (
    options: CerebroApiOptions = {}
): Promise<Omit<UserSettings, 'lastUpdated'>> => {
    const {
        data: { results },
    } = await unscopedApiClient.get(`api/user_settings/`, {
        headers: getCerebroHeaders(options.headers),
    })
    const settings = results?.[0] ?? {}

    return {
        organizationId: settings.organization ?? null,
        organizationGroupId: settings.organization_group ?? null,
        feedbackSurveyLastRead: settings.feedback_survey_last_read ?? null,
        latestNewFeatureId: settings.latest_new_feature_id ?? null,
        hiddenAutomationCapabilities: JSON.parse(
            settings.hidden_automation_capabilities ?? '[]'
        ),
        currency: JSON.parse(settings.currency ?? '{}'),
        homeDashboardId: JSON.parse(settings.home_dashboard_id ?? '{}'),
        homePageMetrics: JSON.parse(settings.home_page_metrics ?? '{}'),
        gilId: settings.gil_id ?? null,
    }
}

export const updateUserSettings = (
    newSettings: UserSettings
): Promise<AxiosResponse> => {
    const payload: Record<string, any> = {}

    if ('organizationId' in newSettings) {
        payload.organization_id = newSettings.organizationId
    }
    if ('organizationGroupId' in newSettings) {
        payload.organization_group_id = newSettings.organizationGroupId
    }
    if ('feedbackSurveyLastRead' in newSettings) {
        payload.feedback_survey_last_read = newSettings.feedbackSurveyLastRead
    }
    if ('latestNewFeatureId' in newSettings) {
        payload.latest_new_feature_id = newSettings.latestNewFeatureId
    }
    if ('hiddenAutomationCapabilities' in newSettings) {
        payload.hidden_automation_capabilities = JSON.stringify(
            newSettings.hiddenAutomationCapabilities
        )
    }
    if ('currency' in newSettings) {
        payload.currency = JSON.stringify(newSettings.currency)
    }
    if ('homeDashboardId' in newSettings) {
        payload.home_dashboard_id = JSON.stringify(newSettings.homeDashboardId)
    }
    if ('homePageMetrics' in newSettings) {
        payload.home_page_metrics = JSON.stringify(newSettings.homePageMetrics)
    }

    return unscopedApiClient.post(`api/user_settings/`, payload)
}
